import clsx from "clsx";
import debounce from "lodash/debounce";
import React, { useEffect, useRef, useState } from "react";

import CommentIcon from "@material-ui/icons/Comment";
import WarningIcon from "@material-ui/icons/ErrorOutline";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DASH } from "common/constants";
import ViewComments from "components/EIDData/ViewComments";
import Loader from "components/Loader";
import { useRacehorse360Api } from "hooks/api";
import { SortOrder } from "interfaces/SortOrder";
import { isFutureDate, transformViewDate } from "utils/date-utils";
import {
  parseCourseSurfaceForPC,
  parseInjuryTrackCondition,
  parseInjuryType
} from "utils/enum-parser";
import { getPersonFullName } from "utils/person";
import { IScrollPositions } from "../../HorseInfoContainer";
import EntryHeaderInfo from "../EntryHeaderInfo";
import { checkSelectedTab, EExamInfoTabs, insertCharToString } from "../helper";
import HorseInfoTable from "../HorseInfoTable";
import { IHorseInfoTableData } from "../HorseInfoTable/HorseInfoTable";
import HorseInfoTabs from "../HorseInfoTabs";

import useStyles from "./styles";

interface IProps {
  horse: racehorse360.IHorse;
  currentTab?: EExamInfoTabs;
  onSetCurrentTab?: (value: EExamInfoTabs) => void;
  onSetTabPosition?: (value: IScrollPositions) => void;
  tabPositions?: IScrollPositions;
}

interface IInjuryTableContent extends IHorseInfoTableData {
  Type: string;
  Surface: string;
  Condition: string;
  Trainer: string;
  "Jockey-Rider": string;
  Official: string;
  "Private Vet": string;
  Medications: string;
  /* TODO: should uncomment these fields when they will be implemented in Injury entity */
  // Limb: string;
  // Diagnosis: string;
  // Outcome: string;
}

interface IInjury {
  id: string;
  facilityName: string;
  incidentDate: string;
  comments: React.ReactNode;
  tableContent: IInjuryTableContent;
}

interface IListItemTableContent extends IHorseInfoTableData {
  Reason: string;
  Track: string;
  "Date On": React.ReactNode;
  "Date Off": React.ReactNode;
}

interface IHorseOnListItem {
  id: string;
  listType: string;
  isFinished: boolean;
  tableContent: IListItemTableContent;
}

const tabs: EExamInfoTabs[] = [EExamInfoTabs.EID, EExamInfoTabs.LISTS];

const Health = (props: IProps) => {
  const { horse, currentTab, onSetCurrentTab, tabPositions, onSetTabPosition } =
    props;
  const classes = useStyles();
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const [selectedTab, setSelectedTab] = useState<EExamInfoTabs>(
    currentTab || tabs[0]
  );

  const { isEID, isLists } = checkSelectedTab(selectedTab);
  const { useListHorseInjuries, useListHorseOnLists } = useRacehorse360Api();

  const renderNoResults = (text: string) => {
    return <div className={classes.noResults}>{text}</div>;
  };

  const renderHorseOnListDates = (
    date: string,
    isFinished: boolean,
    isClosedInFuture: boolean = false
  ) => {
    const className = !isFinished ? classes.warning : "";

    return (
      <span
        className={clsx(className, {
          [classes.italic]: isClosedInFuture
        })}
      >
        {date}
      </span>
    );
  };

  const { isLoading: isLoadingHorsesOnList, data: horseOnListData } =
    useListHorseOnLists("infinite-horses-on-list", {
      query: {
        horseId: {
          value: horse.id
        }
      },
      getOptions: {
        select: [
          "id",
          "listType",
          "dateOn",
          "dateOff",
          "reason",
          "facility.code"
        ],
        orderBy: [`dateOff ${SortOrder.DESC}`, `dateOn ${SortOrder.DESC}`]
      }
    });

  const horseOnLists = horseOnListData?.horseOnLists || [];
  const preparedHorseOnLists: IHorseOnListItem[] = horseOnLists.map(
    (item: racehorse360.IHorseOnList) => {
      const isClosedInFuture = isFutureDate(item.dateOff);
      const isClosed = Boolean(item.dateOff) && !isClosedInFuture;
      return (
        item && {
          id: item.id,
          listType: item.listType,
          isFinished: isClosed,
          tableContent: {
            Reason: item.reason || DASH,
            Track: item.facility?.code || DASH,
            "Date On": renderHorseOnListDates(
              transformViewDate(item.dateOn, null, "M/d/yy") || DASH,
              isClosed
            ),
            "Date Off": renderHorseOnListDates(
              transformViewDate(item.dateOff, null, "M/d/yy") || DASH,
              isClosed,
              isClosedInFuture
            )
          }
        }
      );
    }
  );

  const { isLoading: isLoadingInjuries, data: injuriesData } =
    useListHorseInjuries("infinite-injuries", {
      query: {
        horseIds: [horse.id]
      },
      getOptions: {
        select: [
          "id",
          "incidentDate",
          "facility.name",
          "comments",
          "injuryType",
          "courseSurface",
          "trackCondition",
          "trainer.firstName",
          "trainer.lastName",
          "jockey.firstName",
          "jockey.lastName",
          "officialVet",
          "attendingVet",
          "medications"
        ],
        orderBy: [`createdOn ${SortOrder.DESC}`]
      }
    });

  const injuries = injuriesData?.horseInjuries || [];

  const preparedInjuries: IInjury[] = injuries.map(
    (item: racehorse360.IHorseInjury) => {
      const { trainer } = item;

      return (
        item && {
          id: item.id,
          facilityName: item.facility?.name?.toLowerCase(),
          comments: item.comments,
          incidentDate: item.incidentDate
            ? insertCharToString(
                transformViewDate(item.incidentDate, null, "PP"),
                ".",
                3
              )
            : DASH,
          tableContent: {
            Type: parseInjuryType(item?.injuryType) || DASH,
            Surface: parseCourseSurfaceForPC(item?.courseSurface) || DASH,
            Condition: parseInjuryTrackCondition(item?.trackCondition) || DASH,
            Trainer: getPersonFullName(trainer, "%f %L"),
            "Jockey-Rider": getPersonFullName(trainer, "%f %L"),
            Official: item?.officialVet || DASH,
            "Private Vet": item?.attendingVet || DASH,
            Medications: item?.medications || DASH
            /* TODO: should uncomment and finish these fields when they will be implemented in Injury entity */
            // Limb: "N/A",
            // Diagnosis: "N/A",
            // Outcome: "N/A"
          }
        }
      );
    }
  );

  const handleSelectTab = (value: EExamInfoTabs) => {
    setSelectedTab(value);
    onSetCurrentTab && onSetCurrentTab(value);
  };

  const renderEidTabContent = () => {
    if (!preparedInjuries.length) {
      return renderNoResults("No EID Data");
    }

    const renderViewCommentComponent = (index: number) => {
      return (
        <ViewComments
          buttonClassName={classes.viewCommentsButton}
          horseInjury={injuries[index]}
          icon={<CommentIcon />}
        />
      );
    };

    return preparedInjuries.map((injury: IInjury, index: number) => {
      return (
        <div key={injury.id} className={classes.entry}>
          {/*TODO: should add weather on incident date as extraInfo instead empty line*/}
          <EntryHeaderInfo
            date={injury.incidentDate}
            facilityName={injury.facilityName}
            extraInfo={""}
            sideInfoButton={renderViewCommentComponent(index)}
          />
          <HorseInfoTable data={injury.tableContent} rowHeight={41} />
        </div>
      );
    });
  };

  const renderListTabContent = () => {
    if (!preparedHorseOnLists.length) {
      return renderNoResults("No Lists");
    }

    return preparedHorseOnLists.map((item: IHorseOnListItem) => {
      return (
        <div key={item.id} className={classes.entry}>
          <div
            className={clsx(classes.listType, {
              ["warning"]: !item.isFinished
            })}
          >
            {item.listType}
            {!item.isFinished && (
              <WarningIcon className={classes.warningIcon} />
            )}
          </div>
          <HorseInfoTable data={item.tableContent} rowHeight={32} />
        </div>
      );
    });
  };

  const renderContent = () => {
    return isEID ? renderEidTabContent() : renderListTabContent();
  };

  const handleScrollTabContainer = () => {
    const scrollTop = tabContainerRef.current.scrollTop;

    isEID && onSetTabPosition({ ...tabPositions, eidTabPosition: scrollTop });
    isLists &&
      onSetTabPosition({ ...tabPositions, listsTabPosition: scrollTop });
  };

  const isLoading = isLoadingHorsesOnList || isLoadingInjuries;

  useEffect(() => {
    if (isEID) {
      tabContainerRef.current.scrollTop = tabPositions?.eidTabPosition || 0;
    } else {
      tabContainerRef.current.scrollTop = tabPositions?.listsTabPosition || 0;
    }
  }, [selectedTab]);

  return (
    <>
      <HorseInfoTabs
        data={tabs}
        selectedTab={selectedTab}
        onSetSelectedTab={handleSelectTab}
      />
      <div className={classes.scrollableContainer}>
        <div
          className={classes.contentContainer}
          ref={tabContainerRef}
          onScroll={debounce(handleScrollTabContainer, 500)}
        >
          {isLoading ? <Loader /> : renderContent()}
        </div>
      </div>
    </>
  );
};

export default Health;
